import React, { useRef, useEffect, useContext, useState } from "react";
import ChatMessage from "../chat-message/ChatMessage";
import ChatInput from "../chat-input/ChatInput";
import "./ChatPanel.css";
import ChatHeader from "../chat-header/ChatHeader";
import { ChatContext } from "../../context/ChatContext";
import { useParams, useLocation } from "react-router-dom";
import { fetchConversation } from "../../api/chatApi";
import InitialMessage from "../initial-message/InitialMessage";
import LoadingIndicator from "../loading-indicator/LoadingIndicator";
import AlertBox from "../alert-box/AlertBox";

const ChatPanel = () => {
  const { conversationId } = useParams();
  const location = useLocation();
  const messagesContainerRef = useRef(null);
  const { messages, streamMessage, setMessages, error, setError, streamFailed, status, statusType } = useContext(ChatContext);
  const [autoScrollEnabled, setAutoScrollEnabled] = useState(true);
  const [isWaitingForResponse, setIsWaitingForResponse] = useState(false);
  const hasScrolledToHash = useRef(false);

  useEffect(() => {
    const loadMessages = async () => {
      if (conversationId) {
        try {
          const messages = await fetchConversation(conversationId);
          setMessages(messages);
        } catch (error) {
          console.error("Failed to fetch messages:", error);
        }
      }
      else {
        setMessages([]);
      }
    };

    loadMessages();
  }, []);

  // Scroll to a specific message if the URL contains a hash (e.g. #message-123)
  useEffect(() => {
    if (hasScrolledToHash.current || messages.length === 0) return;

    const hash = location.hash;
    if (hash) {
      // Small delay to ensure the DOM has rendered the messages
      const timer = setTimeout(() => {
        const targetElement = document.querySelector(hash);
        if (targetElement) {
          targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Briefly highlight the message so the user sees which one was shared
          targetElement.style.transition = 'background-color 0.3s ease';
          targetElement.style.backgroundColor = 'rgba(144, 202, 249, 0.15)';
          setTimeout(() => {
            targetElement.style.backgroundColor = '';
          }, 2000);
          setAutoScrollEnabled(false);
          hasScrolledToHash.current = true;
        }
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [messages, location.hash]);

  // Update isWaitingForResponse based on the last message
  useEffect(() => {
    const lastMessage = messages[messages.length - 1];
    if (lastMessage?.role === 'user') {
      setIsWaitingForResponse(true);
      // Force scroll to bottom for new user messages
      if (messagesContainerRef.current) {
        messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
      }
    } else if (lastMessage?.role === 'assistant') {
      setIsWaitingForResponse(false);
    }
  }, [messages]);

  // Handle scrolling
  useEffect(() => {
    const handleUserScroll = () => {
      if (messagesContainerRef.current) {
        const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
        const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10;
        setAutoScrollEnabled(isAtBottom);
      }
    };

    const container = messagesContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleUserScroll);
    }

    return () => {
      if (container) {
        container.removeEventListener('scroll', handleUserScroll);
      }
    };
  }, []);

  // Only auto-scroll when there's new content and auto-scroll is enabled
  useEffect(() => {
    if (autoScrollEnabled && messagesContainerRef.current && (streamMessage || messages.length > 0)) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  }, [streamMessage, messages,isWaitingForResponse]);

  const [showInitialMessage, setShowInitialMessage] = useState(false);

  useEffect(() => {
    if (messages.length === 0) {
      const timer = setTimeout(() => {
        setShowInitialMessage(true);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, []);

  return (
    <div className='chat-panel'>
      <div className='chat-header-container'>
        <ChatHeader />
      </div>

      {error && (
        <div className="error-container">
          <AlertBox message={error} onDismiss={() => setError(null)} />
        </div>
      )}

      <div className='chat-messages-container' ref={messagesContainerRef}>
        {messages.length === 0 && showInitialMessage ? (
          <InitialMessage />
        ) : (
          messages.map((msg, index) => (
            <div key={index} className='message-container'>
              <ChatMessage messageId={msg.id} conversationId={conversationId} role={msg.role} content={msg.content} rating={msg.rating} />
            </div>
          ))
        )}
        {streamMessage && (
          <div className='message-container'>
            <ChatMessage
              role='assistant'
              content={""}
              conversationId={conversationId}
              streamMessage={streamMessage}
              isStreaming={true}
            />
          </div>
        )}
        {isWaitingForResponse && !streamMessage && (
          <LoadingIndicator asMessage={true} error={streamFailed} status={status} statusType={statusType} />
        )}
      </div>

      <div className='chat-input-container'>
        <ChatInput />
      </div>
      <div className="disclaimer">
        <p>Note: The assistant may make mistakes. Please provide feedback in </p>
        <a href="https://adobe.enterprise.slack.com/archives/C073F1NC5RT" target="_blank" rel="noopener noreferrer">Slack</a>
      </div>
    </div>
  );
};

export default ChatPanel;
